import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import SvgPaperPlane from '@icons/paper-plane.svg?react';
import SvgCloseThin from '@icons/close-thin.svg?react';

interface ComponentProps {
  /**
   * The draft, owned by the panel: the composer is remounted whenever the panel switches between docked
   * and floating, and a half-typed question must survive that.
   */
  text: string;
  onTextChange: (text: string) => void;
  sending: boolean;
  onSend: (text: string) => void;
  onCancel: () => void;
}

/**
 * The panel's bottom bar (Figma 21756:18672): question field with the send button inside it, over a
 * line saying the answers come from an AI. Shown in both states — before the first question it is
 * the only thing the user can do, which is why the intro screen no longer carries a field of its own.
 *
 * The field is RingilTextArea with its frame stripped: the border, radius and padding belong to the
 * pill around it so the button can sit inside the same box.
 */
const AssistantComposer: React.FC<ComponentProps> = ({ text, onTextChange: setText, sending, onSend, onCancel }) => {
  const { tr } = useTranslationLgs();

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
    setText('');
  };

  return (
    <div className="flex w-full shrink-0 flex-col gap-2 border-0 border-t border-solid border-content-quaternary bg-base-secondary px-4 py-3">
      {/* items-end: as the question grows over several lines, the send button stays by the last one. */}
      <div className="flex w-full items-end gap-2 rounded-xl border border-solid border-content-tertiary py-1.5 pl-3.5 pr-1.5">
        <RingilTextArea
          value={text}
          onChange={setText}
          rows={1}
          autoGrow
          disabled={sending}
          placeholder={tr('Assistant.composerPlaceholder', 'Napište dotaz…')}
          onKeyDown={e => {
            // Enter sends; Shift+Enter keeps the newline for multi-line questions.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          // py-1.5 makes one line exactly the send button's 32px, so a single line sits centred beside it
          // while the row stays bottom-aligned as the text grows. Up to 6 lines (6 × 20px + 12px padding),
          // then it scrolls.
          className="max-h-[8.25rem] resize-none border-none bg-transparent px-0 py-1.5 text-sm leading-5 disabled:opacity-100"
        />
        {sending ? (
          <RingilButton
            mode={'outlined-rectangle'}
            icon={<SvgCloseThin className={'h-4 w-4'} />}
            tooltip={tr('Assistant.cancel', 'Zrušit')}
            onClick={onCancel}
            className={'h-8 w-8 shrink-0 rounded-lg p-0'}
          />
        ) : (
          <RingilButton
            mode={'filled-rectangle'}
            icon={<SvgPaperPlane className={'h-4 w-4'} />}
            tooltip={tr('Assistant.send', 'Odeslat')}
            onClick={submit}
            disabled={text.trim().length === 0}
            className={'h-8 w-8 shrink-0 rounded-lg p-0'}
          />
        )}
      </div>
      <div className="text-[11px] text-content-secondary">{tr('Assistant.aiDisclaimer', 'Odpovídá AI a může se mýlit. Důležité kroky ověřte v návodech.')}</div>
    </div>
  );
};

export default AssistantComposer;
